import {
  BaseQueryFn,
  FetchArgs,
  fetchBaseQuery,
  FetchBaseQueryError,
  FetchBaseQueryMeta,
} from '@reduxjs/toolkit/query';

import {isNotNil} from 'ramda';

import {getWorkspaceFromUri} from '@dms/routes';

import {Nullish} from 'shared/web';

import {resolveWorkspaceTarget} from './resolveWorkspaceTarget';

type FetchBaseQueryOptions = NonNullable<Parameters<typeof fetchBaseQuery>[0]>;

export type WorkspaceFetchArgs = FetchArgs & {
  /** Explicit workspace target; falls back to the workspace derived from the current location. */
  workspace?: string | Nullish;
};

/**
 * Wraps fetchBaseQuery so the workspace host is resolved per request: an endpoint may pass a
 * `workspace` arg (stripped before the request goes out); without one, the workspace derived
 * from the current location applies. When the subdomain cannot carry the workspace (non-api
 * host, e.g. localhost dev), the workspace travels as the X-Workspace header instead.
 */
export const createWorkspaceBaseQuery =
  (
    options: FetchBaseQueryOptions
  ): BaseQueryFn<
    string | WorkspaceFetchArgs,
    unknown,
    FetchBaseQueryError,
    object,
    FetchBaseQueryMeta
  > =>
  (args, api, extraOptions) => {
    const normalizedArgs: WorkspaceFetchArgs = typeof args === 'string' ? {url: args} : args;
    const {workspace: argsWorkspace, ...fetchArgs} = normalizedArgs;

    const {baseUrl, workspaceHeader} = resolveWorkspaceTarget(
      options.baseUrl,
      argsWorkspace ?? getWorkspaceFromUri().workspace
    );

    return fetchBaseQuery({
      ...options,
      baseUrl,
      prepareHeaders: async (headers, prepareHeadersApi) => {
        const prepared = (await options.prepareHeaders?.(headers, prepareHeadersApi)) ?? headers;
        if (isNotNil(workspaceHeader)) {
          prepared.set('X-Workspace', workspaceHeader);
        }
        return prepared;
      },
    })(fetchArgs, api, extraOptions);
  };
